/**
 * DocumentScanner
 * 
 * Tablet-optimized document scanner that captures images from camera
 * and converts them to a multi-page PDF.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Camera, X, Plus, RotateCcw, Check, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui'

interface DocumentScannerProps {
  onComplete: (pdfFile: File) => void
  onCancel: () => void
  documentName?: string
}

interface ScannedPage {
  id: string
  dataUrl: string
  rotation: number
}

const DocumentScanner = ({ onComplete, onCancel, documentName = 'scanned-document' }: DocumentScannerProps) => {
  const [pages, setPages] = useState<ScannedPage[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [cameraStarted, setCameraStarted] = useState(false)
  
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  // Auto-start camera when component mounts
  useEffect(() => {
    if (!cameraStarted) {
      setCameraStarted(true)
      startCameraFn()
    }
    
    // Cleanup on unmount
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
    }
  }, [])

  // Assign stream to video element once it's rendered and stream is available
  useEffect(() => {
    const assignStream = async () => {
      if (isCapturing && videoRef.current && streamRef.current) {
        // Only assign if not already assigned
        if (videoRef.current.srcObject !== streamRef.current) {
          videoRef.current.srcObject = streamRef.current
          try {
            await videoRef.current.play()
          } catch (err) {
            console.error('Error playing video:', err)
          }
        }
      }
    }
    assignStream()
  }, [isCapturing])

  // Start camera function
  const startCameraFn = async () => {
    setCameraError(null)
    try {
      // Check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Kamera støttes ikke i denne nettleseren.')
        return
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' }, // Use back camera, fall back to any
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      })
      
      streamRef.current = stream
      // Set isCapturing first so video element renders, then useEffect will assign stream
      setIsCapturing(true)
    } catch (err: any) {
      console.error('Camera error:', err)
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Kamera-tilgang ble avslått. Vennligst gi tilgang i nettleserinnstillingene.')
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('Ingen kamera funnet på enheten.')
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setCameraError('Kamera er i bruk av en annen app. Lukk andre apper og prøv igjen.')
      } else {
        setCameraError(`Kunne ikke åpne kamera: ${err.message || 'Ukjent feil'}`)
      }
    }
  }
  
  // Wrapper for manual start (used by button)
  const startCamera = useCallback(() => {
    startCameraFn()
  }, [])

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
    setIsCapturing(false)
  }, [])

  // Capture image from camera
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return
    
    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    if (!ctx) return
    
    // Set canvas size to video size
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    // Draw video frame to canvas
    ctx.drawImage(video, 0, 0)
    
    // Get image data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.92)
    
    // Add to pages
    const newPage: ScannedPage = {
      id: `page-${Date.now()}`,
      dataUrl,
      rotation: 0,
    }
    
    setPages(prev => [...prev, newPage])
    
    // Stop camera after capture
    stopCamera()
  }, [stopCamera])

  // Rotate page
  const rotatePage = useCallback((pageId: string) => {
    setPages(prev => prev.map(page => 
      page.id === pageId 
        ? { ...page, rotation: (page.rotation + 90) % 360 }
        : page
    ))
  }, [])

  // Remove page
  const removePage = useCallback((pageId: string) => {
    setPages(prev => prev.filter(page => page.id !== pageId))
  }, [])

  // Convert pages to PDF
  const createPDF = useCallback(async () => {
    if (pages.length === 0) return
    
    setIsProcessing(true)
    
    try {
      // Dynamic import of pdf-lib for smaller initial bundle
      const { PDFDocument, degrees } = await import('pdf-lib')
      
      const pdfDoc = await PDFDocument.create()
      
      for (const page of pages) {
        // Load image
        const imageBytes = await fetch(page.dataUrl).then(res => res.arrayBuffer())
        
        // Embed image based on format
        let image
        if (page.dataUrl.includes('image/png')) {
          image = await pdfDoc.embedPng(imageBytes)
        } else {
          image = await pdfDoc.embedJpg(imageBytes)
        }
        
        // Calculate dimensions (A4 size in points: 595 x 842)
        const pageWidth = 595
        const pageHeight = 842
        
        // Handle rotation
        let imgWidth = image.width
        let imgHeight = image.height
        
        if (page.rotation === 90 || page.rotation === 270) {
          // Swap dimensions for 90/270 rotation
          [imgWidth, imgHeight] = [imgHeight, imgWidth]
        }
        
        // Scale to fit page while maintaining aspect ratio
        const scale = Math.min(pageWidth / imgWidth, pageHeight / imgHeight)
        const scaledWidth = imgWidth * scale
        const scaledHeight = imgHeight * scale
        
        // Add page to PDF
        const pdfPage = pdfDoc.addPage([pageWidth, pageHeight])
        
        // Center image on page
        const x = (pageWidth - scaledWidth) / 2
        const y = (pageHeight - scaledHeight) / 2
        
        // Draw image with rotation
        pdfPage.drawImage(image, {
          x: x + (page.rotation === 90 || page.rotation === 180 ? scaledWidth : 0),
          y: y + (page.rotation === 180 || page.rotation === 270 ? scaledHeight : 0),
          width: page.rotation === 90 || page.rotation === 270 ? scaledHeight : scaledWidth,
          height: page.rotation === 90 || page.rotation === 270 ? scaledWidth : scaledHeight,
          rotate: degrees(-page.rotation),
        })
      }
      
      // Generate PDF bytes
      const pdfBytes = await pdfDoc.save()
      
      // Create File object (create new Uint8Array to ensure proper BlobPart type)
      const pdfData = new Uint8Array(pdfBytes)
      const blob = new Blob([pdfData], { type: 'application/pdf' })
      const fileName = `${documentName}_${new Date().toISOString().split('T')[0]}.pdf`
      const file = new File([blob], fileName, { type: 'application/pdf' })
      
      onComplete(file)
    } catch (err) {
      console.error('PDF creation error:', err)
      alert('Kunne ikke opprette PDF. Prøv igjen.')
    } finally {
      setIsProcessing(false)
    }
  }, [pages, documentName, onComplete])

  // Cleanup on unmount
  const handleCancel = useCallback(() => {
    stopCamera()
    onCancel()
  }, [stopCamera, onCancel])

  // Use portal to render at document body level (above all modals)
  const scannerContent = (
    <div className="fixed inset-0 bg-black z-[9999] flex flex-col">
      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Header */}
      <div className="bg-stone-900 text-white px-4 py-3 flex items-center justify-between">
        <button
          onClick={handleCancel}
          className="p-2 hover:bg-stone-800 rounded-lg transition-colors"
        >
          <X size={24} />
        </button>
        <h2 className="text-lg font-semibold">Skann dokument</h2>
        <div className="w-10" /> {/* Spacer */}
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {isCapturing ? (
          /* Camera view */
          <div className="flex-1 relative bg-black">
            <video
              ref={videoRef}
              className="w-full h-full object-contain"
              playsInline
              muted
            />
            
            {/* Capture overlay */}
            <div className="absolute inset-0 pointer-events-none">
              {/* Document guide frame */}
              <div className="absolute inset-8 border-2 border-white/50 rounded-lg" />
              <div className="absolute inset-8 border-2 border-dashed border-white/30 rounded-lg m-2" />
            </div>
            
            {/* Capture button */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center">
              <button
                onClick={captureImage}
                className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform"
              >
                <div className="w-16 h-16 bg-white border-4 border-stone-900 rounded-full" />
              </button>
            </div>
            
            {/* Cancel capture button */}
            <button
              onClick={stopCamera}
              className="absolute top-4 right-4 px-4 py-2 bg-stone-800/80 text-white rounded-lg"
            >
              Avbryt
            </button>
          </div>
        ) : (
          /* Scanned pages view */
          <div className="flex-1 bg-stone-100 overflow-y-auto p-4">
            {pages.length === 0 ? (
              /* Empty state - show loading or error */
              <div className="flex flex-col items-center justify-center h-full text-center">
                {!cameraError ? (
                  /* Loading state - camera is starting */
                  <>
                    <div className="p-6 bg-emerald-100 rounded-full mb-4">
                      <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-700 mb-2">
                      Starter kamera...
                    </h3>
                    <p className="text-stone-500 mb-6">
                      Vennligst gi tilgang til kamera hvis du blir spurt
                    </p>
                  </>
                ) : (
                  /* Error state - show retry button */
                  <>
                    <div className="p-6 bg-red-100 rounded-full mb-4">
                      <Camera size={48} className="text-red-500" />
                    </div>
                    <h3 className="text-xl font-semibold text-stone-700 mb-2">
                      Kunne ikke starte kamera
                    </h3>
                    <p className="text-stone-500 mb-6 max-w-sm">
                      {cameraError}
                    </p>
                    <Button onClick={startCamera} size="lg">
                      <Camera size={20} className="mr-2" />
                      Prøv igjen
                    </Button>
                  </>
                )}
              </div>
            ) : (
              /* Pages grid */
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {pages.map((page, index) => (
                    <div
                      key={page.id}
                      className="relative bg-white rounded-xl shadow-md overflow-hidden"
                    >
                      {/* Page number */}
                      <div className="absolute top-2 left-2 bg-stone-900/80 text-white text-xs font-bold px-2 py-1 rounded">
                        Side {index + 1}
                      </div>
                      
                      {/* Image */}
                      <img
                        src={page.dataUrl}
                        alt={`Side ${index + 1}`}
                        className="w-full aspect-[3/4] object-cover"
                        style={{ transform: `rotate(${page.rotation}deg)` }}
                      />
                      
                      {/* Action buttons */}
                      <div className="absolute bottom-2 right-2 flex gap-2">
                        <button
                          onClick={() => rotatePage(page.id)}
                          className="p-2 bg-stone-900/80 text-white rounded-lg hover:bg-stone-900 transition-colors"
                          title="Roter"
                        >
                          <RotateCcw size={18} />
                        </button>
                        <button
                          onClick={() => removePage(page.id)}
                          className="p-2 bg-red-600/90 text-white rounded-lg hover:bg-red-600 transition-colors"
                          title="Slett"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Add more pages button */}
                <button
                  onClick={startCamera}
                  className="w-full py-6 border-2 border-dashed border-stone-300 rounded-xl text-stone-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus size={24} />
                  <span className="font-medium">Legg til side</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Footer actions (only show when not capturing and has pages) */}
      {!isCapturing && pages.length > 0 && (
        <div className="bg-white border-t-2 border-stone-200 p-4 flex gap-3">
          <Button
            variant="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={isProcessing}
          >
            Avbryt
          </Button>
          <Button
            onClick={createPDF}
            className="flex-1"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Oppretter PDF...
              </>
            ) : (
              <>
                <Check size={20} className="mr-2" />
                Opprett PDF ({pages.length} {pages.length === 1 ? 'side' : 'sider'})
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )

  // Render via portal to escape modal z-index stacking
  return createPortal(scannerContent, document.body)
}

export default DocumentScanner

